// CORE IMPORTS
import { useState } from "react";

// MATERIAL UI
import { styled, useTheme } from "@mui/system";
import { Box, Button, Divider, Pagination } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

// COMPONENTS
import { formatPrice } from "src/components/portfolio/helpers";
import { LoadingSpinner } from "src/components/application/LoadingSpinner";
import CountryOrCryptoFlag from "src/components/common/CountryOrCryptoFlag";
import ContainedButton from "src/components/common/ContainedButton";
import { ClearIconWrapper } from "../../styles/Trade.styles";
import TargetOrderCancel from "./TargetOrderCancel";
import { NoOrders } from "../../components/NoOrders";

// REDUX
import {
  CurrencyTransactionLineRes,
  GetTargetOrderCtApiArg,
  useGetTargetOrderCtQuery,
} from "src/store/features/api/generated";

// UTILS
import { dateFormatter } from "src/utils/helpers/DateAndTimeHelpers";

// ICONS
import CachedIcon from "@mui/icons-material/Cached";
import EastIcon from "@mui/icons-material/East";

const RecentTradesWrapper = styled("div")(({ theme }) => ({
  width: "100%",
  height: "100%",
  background: "#FFFFFF",
  borderRadius: "20px",
  boxShadow: "0px 4px 20px 0px rgba(173, 192, 203, 0.30)",
}));

export const Wrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  height: "100%",
  width: "100%",
  padding: "24px",
  gap: "12px",
  borderRadius: "20px",
}));

interface IHeaderButtonProps {
  label: string;
  value: string;
  currentPage: string;
  onClick: (value: string) => void;
}

export const HeaderButton = styled(Button)<IHeaderButtonProps>(({ value, currentPage }) => ({
  color: value === currentPage ? "#002035" : "#5C6469",
  background: value === currentPage ? "#F8F9F9" : "#FFFFFF",
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "16px",
  alignItems: "center",
  textTransform: "none",
  padding: "8px",
  gap: "8px",
}));

export const LoadingWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  height: "100%",
}));

export const ContentWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "32px",
  flex: "1 0 0",
  alignSelf: "stretch",
}));

export const ListWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  height: "173px",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "8px",
  alignSelf: "stretch",
}));

export const ListHeader = styled("div")(({ theme }) => ({
  alignSelf: "stretch",
  paddingBottom: "8px",
  color: theme.palette.common.white,
  fontFamily: "Roobert",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "20px",
  letterSpacing: "-0.32px",
  borderBottom: "1px solid #CCC3AE",
}));

export const ListSubtitle = styled("div")(({ theme }) => ({
  alignSelf: "stretch",
  color: "#CCC3AE",
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "16px",
}));

export const CTWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  gap: "8px",
}));

export const CTHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
}));

interface CTHeaderTextProps {
  weight?: number;
  size?: string;
  lineHeight?: string;
  letterSpacing?: string;
  flex?: string;
}
export const CTHeaderText = styled("div")<CTHeaderTextProps>(
  ({ weight, size, lineHeight, flex }) => ({
    display: "flex",
    alignItems: "center",
    color: "#002035",
    flex: flex ?? "",
    fontFamily: "Söhne",
    fontSize: size ?? "14px",
    fontStyle: "normal",
    fontWeight: weight ?? 400,
    lineHeight: lineHeight ?? "17px",
  }),
);

export const SubHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  justifyContent: "space-between",
}));

interface SubHeaderTextProps {
  flex?: string;
}
export const SubHeaderText = styled("div")<SubHeaderTextProps>(({ flex }) => ({
  color: "#7B858B",
  flex: flex ?? "",
  fontFamily: "Söhne",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "14px",
}));

export const LinkWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  padding: "12px 16px",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  alignSelf: "stretch",
  borderRadius: "4px",
}));

export const SeeAllButton = styled(RouterLink)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  fontFamily: "Roobert",
  fontSize: "18px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "22px",
}));

const ActiveTabDot = styled("span")(({ theme }) => ({
  width: "8px",
  height: "8px",
  borderRadius: "50%",
  backgroundColor: "#0BEC9F",
  marginLeft: "6px",
}));

const CancelButton = styled(Button)(({ theme }) => ({
  fontFamily: "Roobert",
  fontSize: "16px",
  lineHeight: "20px",
  textTransform: "none",
  display: "flex",
  padding: "8px 12px",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  alignSelf: "stretch",
  borderRadius: "8px",
  border: "1px solid #E5E9EB",
  "&:hover": {
    backgroundColor: "#F8F9F9",
  },
}));

const INITIAL_CANCEL_MODAL_DATA = {
  ctId: null,
  type: "",
  tradeUnits: 0,
  currencyTradeCode: "",
  currencyConvertCode: "",
  targetPrice: 0,
};

export const OrderHistoryTargets = () => {
  const theme = useTheme();

  const [modalPage, setModalPage] = useState<string>("inProgress");
  const [cancelModalOpen, setCancelModalOpen] = useState<boolean>(false);

  interface CancelTargetModalProps {
    ctId: string | null;
    type: string;
    tradeUnits: number;
    currencyTradeCode: string;
    currencyConvertCode: string;
    targetPrice: number;
  }
  const [cancelData, setCancelData] = useState<CancelTargetModalProps>(INITIAL_CANCEL_MODAL_DATA);

  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  const [paginationParams, setPaginationParams] = useState<GetTargetOrderCtApiArg>({
    limit: 7,
    page: 1,
    filterType: "all",
    sortType: "date",
    statusFilter: "inProgress",
  });

  const {
    data: recentTrades,
    isFetching,
    refetch: refetchCTS,
  } = useGetTargetOrderCtQuery(paginationParams);

  const handlePagination = (event: unknown, newPage: number): void => {
    setPaginationParams((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handlePageChange = (modalPage: "inProgress" | "orderHistory") => {
    setPaginationParams((prev) => ({
      ...prev,
      page: 1,
      statusFilter: modalPage === "inProgress" ? "inProgress" : "orderHistory",
    }));
    setModalPage(modalPage);
  };

  const handleRefetch = async () => {
    setIsRefreshing(true);

    try {
      await refetchCTS();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleCancelModalOpen = ({
    ctId,
    type,
    tradeUnits,
    currencyTradeCode,
    currencyConvertCode,
    targetPrice,
  }: CancelTargetModalProps) => {
    if (cancelModalOpen) {
      setCancelData(INITIAL_CANCEL_MODAL_DATA);
      setCancelModalOpen(false);
    } else {
      setCancelData({
        ctId,
        type,
        tradeUnits,
        currencyTradeCode,
        currencyConvertCode,
        targetPrice,
      });
      setCancelModalOpen(true);
    }
  };

  interface CTListItemProps {
    type: "buy" | "sell";
    date: string;
    value: number;
    status: string;
    ctId: string;
    buyLine: CurrencyTransactionLineRes;
    sellLine: CurrencyTransactionLineRes;
    percentageDifference?: number;
    handleCancelModalOpen: (data: CancelTargetModalProps) => void;
  }
  const CTListItem = ({
    type,
    date,
    value,
    buyLine,
    sellLine,
    ctId,
    status,
    percentageDifference,
    handleCancelModalOpen,
  }: CTListItemProps) => {
    const activeLine = type === "buy" ? buyLine : sellLine;
    const inactiveLine = type === "buy" ? sellLine : buyLine;
    const units = activeLine.units;
    const targetPrice = activeLine.price;
    const priceCurrency = type === "buy" ? sellLine.currencyConfig : buyLine.currencyConfig;

    const formattedTargetPrice =
      priceCurrency.type === "Fiat"
        ? formatPrice(targetPrice, priceCurrency.code)
        : `${targetPrice} ${priceCurrency.code}`;

    const total =
      inactiveLine.currencyConfig.type === "Fiat"
        ? formatPrice(value, inactiveLine.currencyConfig.code)
        : `${value} ${inactiveLine.currencyConfig.code}`;

    const displayLogo = activeLine.currencyConfig.logoUrl;

    const unitText = units === 1 ? "unit" : "units";
    const newStatus = status.split(" ").pop() || "";

    const formattedPercentageDifference = percentageDifference
      ? `${percentageDifference > 0 ? "+" : ""}${percentageDifference.toFixed(2)}%`
      : "";

    const handleCancel = () => {
      handleCancelModalOpen({
        ctId,
        type,
        tradeUnits: units,
        currencyTradeCode: activeLine.currencyConfig.code,
        currencyConvertCode:
          type === "buy" ? sellLine.currencyConfig.code : buyLine.currencyConfig.code,
        targetPrice,
      });
    };

    return (
      <CTWrapper>
        <CTHeader>
          <CTHeaderText>
            {sellLine.currencyConfig.code}
            <EastIcon sx={{ width: "9px", marginX: "4px" }} />
            {buyLine.currencyConfig.code}
          </CTHeaderText>
          <CTHeaderText>
            {units} {unitText}
            <span style={{ marginLeft: "4px" }} />
            <CountryOrCryptoFlag name={displayLogo} size={16} />
          </CTHeaderText>
        </CTHeader>

        <SubHeader>
          <SubHeaderText>{date}</SubHeaderText>
          <SubHeaderText>{total}</SubHeaderText>
        </SubHeader>

        <SubHeader>
          <SubHeaderText>Status</SubHeaderText>
          <SubHeaderText>{newStatus}</SubHeaderText>
        </SubHeader>

        <SubHeader>
          <SubHeaderText>Target Price</SubHeaderText>
          <SubHeaderText>{formattedTargetPrice}</SubHeaderText>
        </SubHeader>

        {modalPage === "inProgress" && percentageDifference !== undefined && (
          <SubHeader>
            <SubHeaderText>Target Distance</SubHeaderText>
            <SubHeaderText>{formattedPercentageDifference}</SubHeaderText>
          </SubHeader>
        )}

        {newStatus === "Open" && <CancelButton onClick={handleCancel}>Cancel</CancelButton>}
      </CTWrapper>
    );
  };

  return (
    <RecentTradesWrapper>
      <Wrapper>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            alignItems: "center",
          }}>
          <Box>
            <HeaderButton
              label="In Progress"
              value="inProgress"
              currentPage={modalPage}
              onClick={() => handlePageChange("inProgress")}
              role="tab"
              aria-selected={modalPage === "inProgress"}>
              In Progress
              <ActiveTabDot />
            </HeaderButton>

            <HeaderButton
              label="Order History"
              value="orderHistory"
              currentPage={modalPage}
              onClick={() => handlePageChange("orderHistory")}
              role="tab"
              aria-selected={modalPage === "orderHistory"}>
              Order History
            </HeaderButton>
          </Box>
          {/* Todo for button - mimic all states for clear button in swap */}
          <ContainedButton
            id="refresh-button"
            color="neutral"
            borderRadius="20px"
            sx={{
              boxShadow: "none",
              border: "1px solid #E5E9EB",
              color: "#002035",
              width: "auto",
              fontWeight: 500,
              maxHeight: "32px",
              padding: "4px 8px",
              cursor: "pointer",
              gap: "0px",
              pointerEvents: "auto",
              transition: "all 0.2s",
              "&:hover": {
                color: theme.palette.mode === "light" ? "#002035" : "#FFFFFF",
                backgroundColor: "#F8F9F9",
              },
              "&:active": {
                backgroundColor: "#E5E9EB",
              },
              "&:disabled": {
                backgroundColor: "#F8F9F9",
                color: "#A0A4A8",
              },
            }}
            endIcon={
              <ClearIconWrapper isLoading={isRefreshing} data-testid="refresh-button">
                <CachedIcon fontSize="small" sx={{ transform: "scaleX(-1)" }} />
              </ClearIconWrapper>
            }
            disabled={isFetching}
            onClick={() => handleRefetch()}
            data-testid="refresh-button"
            aria-label="Refresh data">
            <></>
          </ContainedButton>
        </Box>
        <Divider sx={{ color: "#E5E7E8", width: "100%", marginBottom: "16px" }} />
        {isFetching ? (
          <LoadingWrapper>
            <LoadingSpinner size={40} />
          </LoadingWrapper>
        ) : (
          <ContentWrapper>
            {recentTrades?.data && recentTrades?.data?.length > 0 ? (
              recentTrades?.data.map((trade: any) => {
                const sellLine = trade.lines.find((line: any) => line.side === "Sell");
                const buyLine = trade.lines.find((line: any) => line.side === "Buy");

                if (!sellLine || !buyLine) return null;

                return (
                  <CTListItem
                    key={trade.id}
                    type={trade.targetType}
                    ctId={trade.id}
                    date={dateFormatter(trade.created, false, true)}
                    value={trade.value}
                    status={trade.status}
                    sellLine={sellLine}
                    buyLine={buyLine}
                    percentageDifference={trade.percentageDifference}
                    handleCancelModalOpen={handleCancelModalOpen}
                  />
                );
              })
            ) : (
              <NoOrders modalPage={modalPage} tradeType="target" />
            )}
          </ContentWrapper>
        )}
        {!isFetching && recentTrades && recentTrades?.data?.length > 0 && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              paddingTop: "27px",
              bottom: "20px",
            }}>
            <Pagination
              count={recentTrades.totalPages}
              page={recentTrades.currentPage}
              onChange={handlePagination}
              showFirstButton
              showLastButton
            />
          </Box>
        )}
        <TargetOrderCancel
          cancelReq={cancelData}
          refetchData={refetchCTS}
          open={cancelModalOpen}
          handleClose={() => setCancelModalOpen(false)}
        />
      </Wrapper>
    </RecentTradesWrapper>
  );
};
